import { motion } from "framer-motion";
import { Home, Building2, HardHat, MapPin } from "lucide-react";
import { PACKAGES } from "@/data/site";
import { Reveal, SectionHead, useLang } from "./common";

const Tier = ({ tier, dark, idx, pkg }) => {
  const { t } = useLang();
  const cap = tier.cap;
  const capText = idx === 0 ? t.install.upto : t.install.above;
  const Icon = idx === 0 ? Home : Building2;
  return (
    <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.3 }} data-testid={`tier-${pkg}-${idx}`}
      className={`rounded-3xl p-6 sm:p-7 ${dark ? "bg-white/[0.06] border border-white/10" : "bg-[#F8F9FA] border border-[#0F295B]/10"}`}>
      <div className="flex items-center gap-3 mb-6">
        <span className="w-10 h-10 rounded-full bg-[#F39C12] grid place-items-center"><Icon className="w-5 h-5 text-white" /></span>
        <span className={`font-display text-lg ${dark ? "text-white" : "text-[#0F295B]"}`}>{capText}</span>
      </div>
      <div className="space-y-5">
        {tier.rows.map((r, ri) => {
          const name = r.k.includes("Other") ? t.install.other : cap === "Up to 8 kW" && !r.k.includes("Other") ? t.install.tataAavada : t.install.tata;
          return (
            <div key={r.k} className={`flex items-end justify-between gap-4 border-b pb-4 last:border-0 last:pb-0 ${dark ? "border-white/10" : "border-[#0F295B]/10"}`}>
              <div>
                <p className={`font-semibold ${dark ? "text-white" : "text-[#0F295B]"}`}>{name}</p>
                {r.note && <p className={`text-xs mt-1 ${dark ? "text-slate-300" : "text-[#4A5B78]"}`}>{t.install.warranty}</p>}
              </div>
              <p className={`font-display text-2xl sm:text-3xl whitespace-nowrap ${dark ? "text-[#F39C12]" : "text-[#00A651]"}`}>
                ₹{r.v}<span className={`text-xs font-body ml-1 ${dark ? "text-slate-300" : "text-[#4A5B78]"}`}>{t.install.perPW}</span>
              </p>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

const PackageCard = ({ p, idx, dark, delay }) => {
  const { t } = useLang();
  return (
    <Reveal delay={delay} className={`lg:col-span-8 rounded-[2rem] p-6 sm:p-10 ${dark ? "bg-[#113163] border border-white/15 relative overflow-hidden" : "bg-white border border-[#0F295B]/10 shadow-[0_30px_60px_-40px_rgba(15,41,91,0.4)]"}`}>
      <div data-testid={`package-${p.id}`} className="relative">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <span className="font-mono2 text-[11px] tracking-[0.2em] uppercase rounded-full bg-[#F39C12] text-[#0F295B] font-bold px-3 py-1">{t.install.tags[idx]}</span>
        </div>
        <h3 className={`font-display text-2xl sm:text-3xl ${dark ? "text-white" : "text-[#0F295B]"}`}>{t.install.titles[idx]}</h3>
        <p className={`mt-2 text-sm sm:text-base ${dark ? "text-slate-300" : "text-[#4A5B78]"}`}>({t.install.subs[idx]})</p>
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {p.tiers.map((tier, i) => <Tier key={tier.cap} tier={tier} idx={i} dark={dark} pkg={p.id} />)}
        </div>
      </div>
    </Reveal>
  );
};

export const Installation = () => {
  const { t } = useLang();
  return (
    <section id="installation" data-testid="installation-section" className="relative bg-[#0F295B] grain overflow-hidden py-24 sm:py-32">
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
        <SectionHead dark num={t.install.num} kicker={t.install.kicker} segs={t.install.title} desc={t.install.desc} testid="installation-head" />
        <div className="grid lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-4 lg:row-span-2 relative min-h-[360px] rounded-[2rem] overflow-hidden group">
            <img src="/assets/roof.jpg" alt={t.install.imgAlt} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.6s] group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F295B]/90 via-[#0F295B]/10 to-transparent" />
            <div className="absolute bottom-0 p-7 text-white">
              <p className="font-mono2 text-xs tracking-[0.2em] uppercase text-[#F39C12]">{t.install.imgKicker}</p>
              <p className="font-display text-2xl mt-2 leading-tight">{t.install.imgTitle}</p>
            </div>
          </Reveal>
          <PackageCard p={PACKAGES[0]} idx={0} delay={0.05} />
          <PackageCard p={PACKAGES[1]} idx={1} dark delay={0.1} />
          <Reveal className="lg:col-span-12 grid md:grid-cols-2 gap-4" delay={0.1}>
            <div data-testid="note-labour" className="flex items-center gap-4 rounded-3xl bg-[#F39C12]/15 border border-[#F39C12]/40 p-6">
              <HardHat className="w-8 h-8 text-[#F39C12] shrink-0" />
              <p className="text-white"><span className="font-bold text-[#F39C12]">{t.install.important}</span> {t.install.noteLabour}</p>
            </div>
            <div data-testid="note-outstation" className="flex items-center gap-4 rounded-3xl bg-[#00A651]/15 border border-[#00A651]/40 p-6">
              <MapPin className="w-8 h-8 text-[#00A651] shrink-0" />
              <p className="text-white">{t.install.noteOutstation}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
